import type { Problem } from '@/types';

/* Practice Gym ladder: Experimentation & A/B Testing (module m15). Problem-forward, progressive.
   Auto-included via src/data/gym/index.ts; referenced by the `ab` ladder. */
export const abProblems: Problem[] = [
  {
    "id": "ab1",
    "ladder": "ab",
    "pos": 1,
    "stage": "Recognition",
    "lvl": 0,
    "difficulty": "recognition",
    "priority": "required",
    "source": "interview-style",
    "module": "m15",
    "title": "What is the honest read of an A/B test?",
    "concept": ["experiment"],
    "obj": ["Customer", "Charge"],
    "metric": "n/a",
    "edge": ["ITT", "guardrail"],
    "timed": false,
    "est": "3 min",
    "business": "A PM says treatment 'won' — but it's unclear what metric, what denominator, and whether guardrails held.",
    "schema": ["experiment_exposures", "charges"],
    "prompt": "No SQL yet. In plain English: name the primary metric, the ITT denominator, the lift formula, and one guardrail you'd report alongside it.",
    "deliverable": "A short explanation: one pre-registered primary metric, ITT denominator (all exposed), lift = (treatment−control)/control, plus a guardrail.",
    "why": "An A/B read lies through the denominator, peeking and metric-shopping; recognise the honest version first.",
    "harder": "First rung — name the metric, the ITT denominator, lift and a guardrail.",
    "before": [
      "Dividing by 'those who acted' biases the comparison — use all exposed (ITT).",
      "A conversion win that raises disputes/refunds isn't a win — report a guardrail."
    ],
    "hints": [
      "Primary metric is chosen before the test; lift is relative to control.",
      "ITT denominator = everyone assigned to the arm."
    ],
    "model": "An A/B test randomly splits exposed users into control and treatment and compares ONE pre-registered primary metric. Compute it intention-to-treat: numerator = converters, denominator = ALL exposed in the arm (never just those who acted, which re-introduces selection bias). Report lift = (treatment_rate − control_rate)/control_rate, and always pair it with a guardrail (dispute or refund rate). The read is only trustworthy on a fixed horizon (no peeking) and a single primary metric (no metric-shopping), and you check the lift is practically, not just statistically, significant.",
    "grain": "Conceptual — no output rows.",
    "confusion": "Treatment-on-treated (dividing by actors) is the classic biased denominator.",
    "explain": "Say the four pieces aloud: primary metric, ITT denominator, lift, guardrail.",
    "teaches": "Recognising the ITT denominator, lift and guardrails before SQL.",
    "mode": "SQL"
  },
  {
    "id": "ab2",
    "ladder": "ab",
    "pos": 2,
    "stage": "Mechanical",
    "lvl": 1,
    "difficulty": "easy",
    "priority": "required",
    "source": "pgexercises-style",
    "module": "m15",
    "title": "Exposed customers per variant",
    "concept": ["experiment"],
    "obj": ["Customer"],
    "metric": "count",
    "edge": ["balance"],
    "timed": false,
    "est": "4 min",
    "business": "Before any rate, confirm the arms are roughly balanced: how many distinct customers are in each variant?",
    "schema": ["experiment_exposures"],
    "prompt": "For experiment 'checkout_v2', return the count of distinct exposed customers per variant.",
    "deliverable": "One row per variant: variant, exposed.",
    "why": "Arm balance is the first sanity check; the exposed count is also the ITT denominator.",
    "harder": "Introduces the exposed population the rest of the ladder divides by.",
    "before": ["DISTINCT customer_id per variant; filter to the one experiment."],
    "hints": [
      "WHERE experiment='checkout_v2'.",
      "GROUP BY variant, COUNT(DISTINCT customer_id)."
    ],
    "solution": "SELECT variant, COUNT(DISTINCT customer_id) AS exposed\nFROM experiment_exposures\nWHERE experiment='checkout_v2'\nGROUP BY variant\nORDER BY variant;",
    "grain": "One row per variant.",
    "confusion": "This count is the ITT denominator — not the number who converted.",
    "explain": "State that exposed is the denominator for every later rate.",
    "teaches": "Defining the exposed population per arm.",
    "mode": "SQL"
  },
  {
    "id": "ab3",
    "ladder": "ab",
    "pos": 3,
    "stage": "Simple application",
    "lvl": 2,
    "difficulty": "medium",
    "priority": "required",
    "source": "Mode-style",
    "module": "m15",
    "title": "ITT conversion rate per variant",
    "concept": ["experiment"],
    "obj": ["Customer", "Charge"],
    "metric": "conversion rate",
    "edge": ["ITT denominator", "zero denominator"],
    "timed": false,
    "est": "8 min",
    "business": "Compute each arm's conversion: exposed customers who later made a succeeded charge, over all exposed.",
    "schema": ["experiment_exposures", "charges"],
    "prompt": "Per variant, return exposed, converted (distinct exposed customers with a succeeded charge) and conversion_itt = converted ÷ exposed.",
    "deliverable": "One row per variant: variant, exposed, converted, conversion_itt.",
    "why": "Dividing by all exposed (not by converters/actors) is the load-bearing ITT detail.",
    "harder": "Introduces the LEFT JOIN from all exposed to converters so the denominator stays whole.",
    "before": [
      "exposed CTE = distinct (customer, variant); converted = exposed customers with a succeeded charge.",
      "Divide by COUNT(DISTINCT exposed), not by converters."
    ],
    "howto": [
      "exposed CTE; converted CTE (DISTINCT exposed customers, succeeded charge).",
      "LEFT JOIN exposed → converted; rate = converted/exposed guarded."
    ],
    "hints": [
      "LEFT JOIN keeps non-converters in the denominator.",
      "ROUND(converted::numeric/NULLIF(exposed,0),4)."
    ],
    "solution": "WITH exposed AS (\n  SELECT DISTINCT customer_id, variant FROM experiment_exposures WHERE experiment='checkout_v2'\n),\nconverted AS (\n  SELECT DISTINCT e.variant, c.customer_id\n  FROM charges c JOIN exposed e USING (customer_id)\n  WHERE c.status='succeeded'\n)\nSELECT e.variant,\n       COUNT(DISTINCT e.customer_id)  AS exposed,\n       COUNT(DISTINCT cv.customer_id) AS converted,\n       ROUND(COUNT(DISTINCT cv.customer_id)::numeric / NULLIF(COUNT(DISTINCT e.customer_id),0), 4) AS conversion_itt\nFROM exposed e\nLEFT JOIN converted cv ON cv.variant=e.variant AND cv.customer_id=e.customer_id\nGROUP BY e.variant\nORDER BY e.variant;",
    "verify": {
      "grain": "One row per variant.",
      "columns": ["variant", "exposed", "converted", "conversion_itt"],
      "sample": { "cols": ["variant", "exposed", "converted", "conversion_itt"], "rows": [["control", "5000", "2800", "0.5600"], ["treatment", "5020", "3012", "0.6000"]] },
      "commonWrong": ["Dividing by converters or actors (treatment-on-treated).", "INNER JOIN dropping non-converters from the denominator."],
      "validation": ["converted ≤ exposed", "conversion_itt ∈ [0,1]"],
      "edgeCases": ["An arm with zero converters → rate 0, not an error (NULLIF).", "A customer with several succeeded charges counts once (DISTINCT)."],
      "checklist": ["exposed denominator", "DISTINCT converters", "guarded rate"]
    },
    "confusion": "ITT divides by ALL exposed; converters in the numerator are distinct customers.",
    "explain": "Name the numerator and denominator before writing the divide.",
    "teaches": "The ITT conversion rate per arm.",
    "mode": "SQL"
  },
  {
    "id": "ab4",
    "ladder": "ab",
    "pos": 4,
    "stage": "Applied metric",
    "lvl": 3,
    "difficulty": "medium",
    "priority": "required",
    "source": "DataLemur-style",
    "module": "m15",
    "title": "Lift: treatment vs control",
    "concept": ["experiment"],
    "obj": ["Customer", "Charge"],
    "metric": "lift",
    "edge": ["pivot", "zero denominator"],
    "timed": false,
    "est": "8 min",
    "business": "Leadership wants a single number: the relative lift of treatment over control.",
    "schema": ["experiment_exposures", "charges"],
    "prompt": "Return one row with control_rate, treatment_rate and lift = (treatment − control) ÷ control, computed on ITT rates.",
    "deliverable": "One summary row: control_rate, treatment_rate, lift.",
    "why": "Pivoting per-arm rates and computing a guarded relative lift is the headline deliverable.",
    "harder": "Adds a pivot (FILTER) of the per-variant rates into one row plus the lift formula.",
    "before": [
      "Compute per-variant ITT rates in a CTE, then pivot control vs treatment with FILTER.",
      "lift = (treatment − control)/NULLIF(control,0)."
    ],
    "howto": [
      "rates CTE: per-variant ITT rate.",
      "MAX(rate) FILTER (WHERE variant='control'/'treatment') to pivot.",
      "lift = (treatment − control)/NULLIF(control,0)."
    ],
    "hints": [
      "FILTER (WHERE variant=...) pivots a long table to one row.",
      "Guard the control rate in the denominator with NULLIF."
    ],
    "solution": "WITH exposed AS (\n  SELECT DISTINCT customer_id, variant FROM experiment_exposures WHERE experiment='checkout_v2'\n),\nconverted AS (\n  SELECT DISTINCT e.variant, c.customer_id FROM charges c JOIN exposed e USING (customer_id) WHERE c.status='succeeded'\n),\nrates AS (\n  SELECT e.variant,\n         COUNT(DISTINCT cv.customer_id)::numeric / NULLIF(COUNT(DISTINCT e.customer_id),0) AS rate\n  FROM exposed e\n  LEFT JOIN converted cv ON cv.variant=e.variant AND cv.customer_id=e.customer_id\n  GROUP BY e.variant\n)\nSELECT ROUND(MAX(rate) FILTER (WHERE variant='control'),4)   AS control_rate,\n       ROUND(MAX(rate) FILTER (WHERE variant='treatment'),4)  AS treatment_rate,\n       ROUND((MAX(rate) FILTER (WHERE variant='treatment') - MAX(rate) FILTER (WHERE variant='control'))\n             / NULLIF(MAX(rate) FILTER (WHERE variant='control'),0), 4) AS lift\nFROM rates;",
    "verify": {
      "grain": "A single summary row.",
      "columns": ["control_rate", "treatment_rate", "lift"],
      "sample": { "cols": ["control_rate", "treatment_rate", "lift"], "rows": [["0.5600", "0.6000", "0.0714"]] },
      "commonWrong": ["Reporting the absolute difference and calling it lift.", "Dividing by treatment instead of control."],
      "validation": ["lift sign matches which rate is larger", "rates ∈ [0,1]"],
      "edgeCases": ["Control rate 0 → NULLIF guards the lift.", "Equal rates → lift 0."],
      "checklist": ["ITT rates", "pivot with FILTER", "relative lift over control"]
    },
    "confusion": "Relative lift divides the difference by the control rate, not the treatment rate.",
    "explain": "State the lift formula and which arm is the denominator.",
    "teaches": "Pivoting arm rates and computing relative lift.",
    "mode": "SQL"
  },
  {
    "id": "ab5",
    "ladder": "ab",
    "pos": 5,
    "stage": "Multi-step",
    "lvl": 4,
    "difficulty": "hard",
    "priority": "should",
    "source": "Mode-style",
    "module": "m15",
    "title": "Primary metric plus a guardrail",
    "concept": ["experiment"],
    "obj": ["Customer", "Charge", "Dispute"],
    "metric": "conversion + guardrail",
    "edge": ["fan-out", "guardrail"],
    "timed": false,
    "est": "11 min",
    "business": "Before declaring a winner, check the conversion lift didn't come with more disputes.",
    "schema": ["experiment_exposures", "charges", "disputes"],
    "prompt": "Per variant, report ITT conversion AND a guardrail dispute_rate = distinct disputed succeeded charges ÷ succeeded charges. Pre-aggregate to avoid fan-out.",
    "deliverable": "One row per variant: variant, conversion_itt, dispute_rate.",
    "why": "A defensible decision pairs the primary metric with a guardrail in the same read.",
    "harder": "Combines the ITT rate with a second, fan-out-prone guardrail metric.",
    "before": [
      "Conversion is the ITT rate from earlier; the guardrail is over succeeded charges in the arm.",
      "Count DISTINCT disputed charge_ids so multiple disputes don't inflate it."
    ],
    "howto": [
      "exposed and converted CTEs as before.",
      "charges_in_arm CTE: succeeded charges per variant (their charge_ids).",
      "dispute_rate = distinct disputed ÷ distinct succeeded, guarded."
    ],
    "hints": [
      "Build succeeded charges per arm in their own CTE to control the join grain.",
      "LEFT JOIN disputes on charge_id; COUNT(DISTINCT d.charge_id)."
    ],
    "solution": "WITH exposed AS (\n  SELECT DISTINCT customer_id, variant FROM experiment_exposures WHERE experiment='checkout_v2'\n),\nconverted AS (\n  SELECT DISTINCT e.variant, c.customer_id FROM charges c JOIN exposed e USING (customer_id) WHERE c.status='succeeded'\n),\narm_charges AS (\n  SELECT e.variant, c.charge_id\n  FROM charges c JOIN exposed e USING (customer_id)\n  WHERE c.status='succeeded'\n)\nSELECT e.variant,\n       ROUND(COUNT(DISTINCT cv.customer_id)::numeric / NULLIF(COUNT(DISTINCT e.customer_id),0),4) AS conversion_itt,\n       ROUND(COUNT(DISTINCT d.charge_id)::numeric / NULLIF(COUNT(DISTINCT ac.charge_id),0),4)     AS dispute_rate\nFROM exposed e\nLEFT JOIN converted cv  ON cv.variant=e.variant AND cv.customer_id=e.customer_id\nLEFT JOIN arm_charges ac ON ac.variant=e.variant\nLEFT JOIN disputes d     ON d.charge_id=ac.charge_id\nGROUP BY e.variant\nORDER BY e.variant;",
    "verify": {
      "grain": "One row per variant.",
      "columns": ["variant", "conversion_itt", "dispute_rate"],
      "sample": { "cols": ["variant", "conversion_itt", "dispute_rate"], "rows": [["control", "0.5600", "0.0080"], ["treatment", "0.6000", "0.0140"]] },
      "commonWrong": ["Reporting only conversion and ignoring the guardrail.", "Fan-out from joining disputes without DISTINCT."],
      "validation": ["both rates ∈ [0,1]", "guardrail computed over succeeded charges, not exposed"],
      "edgeCases": ["Treatment converts more but disputes more — the 'win' is questionable.", "An arm with no disputes → guardrail 0."],
      "checklist": ["ITT conversion", "guardrail over succeeded charges", "DISTINCT disputed charges"]
    },
    "confusion": "The guardrail denominator is succeeded charges in the arm, not exposed customers.",
    "explain": "Explain why a conversion win with a higher dispute rate may not be a real win.",
    "teaches": "Reporting a primary metric next to a guardrail.",
    "mode": "SQL"
  },
  {
    "id": "ab6",
    "ladder": "ab",
    "pos": 6,
    "stage": "Debug",
    "lvl": 3,
    "difficulty": "medium",
    "priority": "should",
    "source": "interview-style",
    "module": "m15",
    "title": "Debug: treated-only denominator",
    "concept": ["experiment"],
    "obj": ["Customer", "Charge"],
    "metric": "conversion rate",
    "edge": ["ITT", "selection bias"],
    "timed": false,
    "est": "7 min",
    "business": "A conversion comparison divides by customers who attempted a charge — and the lift looks too good.",
    "schema": ["experiment_exposures", "charges"],
    "prompt": "Diagnose the bug in one sentence, then fix the denominator to be all exposed (ITT).",
    "broken": "WITH exposed AS (SELECT DISTINCT customer_id, variant FROM experiment_exposures WHERE experiment='checkout_v2'),\nattempted AS (SELECT DISTINCT e.variant, c.customer_id FROM charges c JOIN exposed e USING (customer_id))\nSELECT a.variant,\n       ROUND(COUNT(DISTINCT c.customer_id) FILTER (WHERE c.status='succeeded')::numeric\n             / NULLIF(COUNT(DISTINCT a.customer_id),0),4) AS conversion\nFROM attempted a\nJOIN charges c ON c.customer_id=a.customer_id\nGROUP BY a.variant;",
    "deliverable": "A one-sentence diagnosis + a corrected ITT query dividing by all exposed.",
    "why": "Conditioning on 'attempted' is post-randomisation selection; treatment changes who attempts, so the arms stop being comparable.",
    "harder": "You must see the denominator, not the numerator, is the bug.",
    "before": ["Attempting is affected by the treatment — so it can't be in the denominator."],
    "hints": [
      "The denominator should be all exposed, not just attempters.",
      "LEFT JOIN exposed → converted and divide by exposed."
    ],
    "solution": "WITH exposed AS (\n  SELECT DISTINCT customer_id, variant FROM experiment_exposures WHERE experiment='checkout_v2'\n),\nconverted AS (\n  SELECT DISTINCT e.variant, c.customer_id\n  FROM charges c JOIN exposed e USING (customer_id)\n  WHERE c.status='succeeded'\n)\nSELECT e.variant,\n       COUNT(DISTINCT e.customer_id)  AS exposed,\n       COUNT(DISTINCT cv.customer_id) AS converted,\n       ROUND(COUNT(DISTINCT cv.customer_id)::numeric / NULLIF(COUNT(DISTINCT e.customer_id),0),4) AS conversion_itt\nFROM exposed e\nLEFT JOIN converted cv ON cv.variant=e.variant AND cv.customer_id=e.customer_id\nGROUP BY e.variant\nORDER BY e.variant;",
    "verify": {
      "grain": "One row per variant.",
      "columns": ["variant", "exposed", "converted", "conversion_itt"],
      "commonWrong": ["Keeping attempters in the denominator.", "Fixing the numerator instead of the denominator."],
      "validation": ["denominator = all exposed", "converted ≤ exposed"],
      "edgeCases": ["If treatment increases attempts, the treated-only rate understates true lift or overstates it — either way it's biased."],
      "checklist": ["named the selection bias", "ITT denominator", "LEFT JOIN keeps non-actors"]
    },
    "confusion": "The numerator was fine; dividing by attempters (a post-treatment subset) was the bug.",
    "explain": "State why 'attempted' is post-randomisation selection before fixing it.",
    "teaches": "Restoring the ITT denominator to remove selection bias.",
    "mode": "SQL"
  },
  {
    "id": "ab7",
    "ladder": "ab",
    "pos": 7,
    "stage": "Edge-case hard",
    "lvl": 4,
    "difficulty": "hard",
    "priority": "stretch",
    "source": "StrataScratch-style",
    "module": "m15",
    "title": "Conversion window and contamination",
    "concept": ["experiment"],
    "obj": ["Customer", "Charge"],
    "metric": "windowed conversion",
    "edge": ["conversion window", "contamination"],
    "timed": false,
    "est": "10 min",
    "business": "Count conversions only within 7 days of first exposure, and exclude customers exposed to both arms.",
    "schema": ["experiment_exposures", "charges"],
    "prompt": "Per variant, ITT conversion where a conversion is a succeeded charge within 7 days of the customer's FIRST exposure; drop customers who appear in both variants (contamination).",
    "deliverable": "One row per variant: variant, exposed, converted, conversion_itt.",
    "why": "First-exposure timing, a conversion window and contamination handling are exactly where experiment reads break.",
    "harder": "Adds a first-exposure timestamp, a windowed conversion and a both-arms exclusion.",
    "before": [
      "Take MIN(exposed_at) per (customer, variant) for first exposure.",
      "Exclude customers whose customer_id appears under more than one variant.",
      "A conversion is succeeded AND created_at within [exposed_at, exposed_at + 7 days)."
    ],
    "howto": [
      "clean CTE: customers with exactly one variant (HAVING COUNT(DISTINCT variant)=1).",
      "first_exp: MIN(exposed_at) per kept (customer, variant).",
      "converted: succeeded charge within 7 days of first exposure; rate ITT."
    ],
    "hints": [
      "Filter contamination with HAVING COUNT(DISTINCT variant)=1 in a customers CTE.",
      "Window: c.created_at >= exposed_at AND c.created_at < exposed_at + INTERVAL '7 days'."
    ],
    "solution": "WITH clean AS (\n  SELECT customer_id\n  FROM experiment_exposures\n  WHERE experiment='checkout_v2'\n  GROUP BY customer_id\n  HAVING COUNT(DISTINCT variant)=1\n),\nfirst_exp AS (\n  SELECT e.customer_id, e.variant, MIN(e.exposed_at) AS exposed_at\n  FROM experiment_exposures e\n  JOIN clean USING (customer_id)\n  WHERE e.experiment='checkout_v2'\n  GROUP BY e.customer_id, e.variant\n),\nconverted AS (\n  SELECT DISTINCT f.variant, f.customer_id\n  FROM first_exp f\n  JOIN charges c ON c.customer_id=f.customer_id\n  WHERE c.status='succeeded'\n    AND c.created_at >= f.exposed_at\n    AND c.created_at <  f.exposed_at + INTERVAL '7 days'\n)\nSELECT f.variant,\n       COUNT(DISTINCT f.customer_id)  AS exposed,\n       COUNT(DISTINCT cv.customer_id) AS converted,\n       ROUND(COUNT(DISTINCT cv.customer_id)::numeric / NULLIF(COUNT(DISTINCT f.customer_id),0),4) AS conversion_itt\nFROM first_exp f\nLEFT JOIN converted cv ON cv.variant=f.variant AND cv.customer_id=f.customer_id\nGROUP BY f.variant\nORDER BY f.variant;",
    "verify": {
      "grain": "One row per variant.",
      "columns": ["variant", "exposed", "converted", "conversion_itt"],
      "sample": { "cols": ["variant", "exposed", "converted", "conversion_itt"], "rows": [["control", "4900", "2650", "0.5408"], ["treatment", "4880", "2830", "0.5799"]] },
      "commonWrong": ["Counting conversions before exposure or outside the window.", "Leaving both-arm customers in (contamination)."],
      "validation": ["converted ≤ exposed", "conversions strictly within the 7-day window"],
      "edgeCases": ["A charge before exposure does not count.", "A customer in both arms is dropped entirely, not assigned to one."],
      "checklist": ["first-exposure timestamp", "7-day conversion window", "contamination excluded"]
    },
    "confusion": "Contaminated customers are removed, not arbitrarily assigned — keeping them biases both arms.",
    "explain": "Explain why conversions must be bounded by the post-exposure window.",
    "teaches": "Windowed conversion and contamination handling.",
    "mode": "SQL"
  },
  {
    "id": "ab8",
    "ladder": "ab",
    "pos": 8,
    "stage": "Final boss",
    "lvl": 5,
    "difficulty": "final-boss",
    "priority": "boss",
    "source": "Stripe-interview-style",
    "module": "m15",
    "title": "Final boss (timed 12 min): experiment readout",
    "concept": ["experiment"],
    "obj": ["Customer", "Charge", "Dispute"],
    "metric": "conversion + lift + guardrail",
    "edge": ["ITT", "window", "guardrail", "peeking"],
    "timed": true,
    "est": "12 min",
    "business": "\"Ship checkout_v2?\" Produce the full readout per arm and defend ITT, the window, the guardrail and the no-peeking/significance caveats.",
    "schema": ["experiment_exposures", "charges", "disputes"],
    "prompt": "Per variant for 'checkout_v2': exposed, ITT conversion (succeeded within 7 days of first exposure), and a dispute-rate guardrail over the arm's succeeded charges. Then state, in words, the three checks before shipping a positive lift.",
    "deliverable": "One row per variant: variant, exposed, conversion_itt, dispute_rate — plus the caveats.",
    "why": "This fuses ITT, the conversion window, the guardrail and the peeking/significance judgment — a complete experiment answer.",
    "harder": "Combines first-exposure timing, the windowed ITT rate, a fan-out-safe guardrail and the ship-decision caveats, under time pressure.",
    "before": [
      "First exposure per (customer, variant) via MIN(exposed_at); ITT denominator = all exposed.",
      "Conversion = succeeded within 7 days of first exposure; guardrail over the arm's succeeded charges.",
      "Caveats: pre-registered single metric, fixed horizon (no peeking), statistical AND business significance."
    ],
    "howto": [
      "first_exp CTE: MIN(exposed_at) per (customer, variant).",
      "converted CTE: windowed succeeded charge; arm_charges CTE: succeeded charge_ids per arm.",
      "Aggregate ITT conversion and DISTINCT-disputed guardrail per variant."
    ],
    "hints": [
      "Reuse the windowed-conversion pattern; add the guardrail over succeeded charges.",
      "Count DISTINCT disputed charge_ids to avoid fan-out.",
      "Be ready to name peeking, multiple comparisons and significance as the ship caveats."
    ],
    "solution": "WITH first_exp AS (\n  SELECT customer_id, variant, MIN(exposed_at) AS exposed_at\n  FROM experiment_exposures WHERE experiment='checkout_v2'\n  GROUP BY customer_id, variant\n),\nconverted AS (\n  SELECT DISTINCT f.variant, f.customer_id\n  FROM first_exp f\n  JOIN charges c ON c.customer_id=f.customer_id\n  WHERE c.status='succeeded'\n    AND c.created_at >= f.exposed_at\n    AND c.created_at <  f.exposed_at + INTERVAL '7 days'\n),\narm_charges AS (\n  SELECT f.variant, c.charge_id\n  FROM first_exp f\n  JOIN charges c ON c.customer_id=f.customer_id\n  WHERE c.status='succeeded'\n    AND c.created_at >= f.exposed_at\n    AND c.created_at <  f.exposed_at + INTERVAL '7 days'\n)\nSELECT f.variant,\n       COUNT(DISTINCT f.customer_id) AS exposed,\n       ROUND(COUNT(DISTINCT cv.customer_id)::numeric / NULLIF(COUNT(DISTINCT f.customer_id),0),4) AS conversion_itt,\n       ROUND(COUNT(DISTINCT d.charge_id)::numeric / NULLIF(COUNT(DISTINCT ac.charge_id),0),4)     AS dispute_rate\nFROM first_exp f\nLEFT JOIN converted cv  ON cv.variant=f.variant AND cv.customer_id=f.customer_id\nLEFT JOIN arm_charges ac ON ac.variant=f.variant\nLEFT JOIN disputes d     ON d.charge_id=ac.charge_id\nGROUP BY f.variant\nORDER BY f.variant;",
    "rubric": [
      "First exposure per (customer, variant); ITT denominator = all exposed",
      "Conversion = succeeded within 7 days of first exposure",
      "Guardrail (dispute rate) computed over the arm's succeeded charges, DISTINCT-safe",
      "Guards (NULLIF) on both rates",
      "Pre-registered single primary metric and fixed horizon (no peeking) named",
      "Lift judged for business as well as statistical significance"
    ],
    "verify": {
      "grain": "One row per variant.",
      "columns": ["variant", "exposed", "conversion_itt", "dispute_rate"],
      "sample": { "cols": ["variant", "exposed", "conversion_itt", "dispute_rate"], "rows": [["control", "5000", "0.5400", "0.0080"], ["treatment", "5020", "0.5800", "0.0150"]] },
      "commonWrong": ["Treated-only denominators.", "Conversions outside the window.", "Reporting conversion with no guardrail or caveats."],
      "validation": ["both rates ∈ [0,1]", "ITT denominators", "guardrail over succeeded charges"],
      "edgeCases": ["Treatment lift is real but its dispute rate doubled — hold the ship.", "Newest cohorts may not have a full 7-day window yet."],
      "checklist": ["ITT", "windowed conversion", "guardrail", "no-peeking/significance caveats"]
    },
    "confusion": "A positive lift is necessary, not sufficient — guardrails and the peeking/significance checks decide the ship.",
    "explain": "Defend ITT, the window, the guardrail and the no-peeking caveat as if to an interviewer.",
    "teaches": "A complete experiment readout with a defensible ship decision.",
    "mode": "SQL"
  }
];
